import React, { useEffect } from 'react';
import { Building } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField, InlineEditText } from '../shared/InlineEditField';
import {
  useReturnPeriod,
  ReturnCell,
  ReturnPeriodHeader,
  totalReturnPct,
  pickPeriodReturn,
} from '../shared/ReturnPeriodControls';
import { UpgradePrompt } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { PrivateEquity } from '../../types';

const RETURN_PERIOD_STORAGE_KEY = 'tallyup_pe_return_period';

interface PrivateEquityWidgetProps {
  onAdd: () => void;
  onUpgradeClick: () => void;
  variant?: 'card' | 'page';
}

export const PrivateEquityWidget: React.FC<PrivateEquityWidgetProps> = ({
  onAdd,
  onUpgradeClick,
  variant = 'card',
}) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { totalPrivateEquity, updatePrivateEquityHolding, handleDeletePrivateEquity, handleBatchDelete } = useFinancialData();
  const { visiblePrivateEquity, hasHiddenPrivateEquity, hiddenPrivateEquityCount, isAtPrivateEquityLimit } =
    useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit, completeEditText } =
    useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('privateEquity');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeletePrivateEquity(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Private Equity', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, visiblePrivateEquity.length, handleBatchDeleteAsync);
  const {
    returnPeriod,
    setPeriod,
    periodMenuOpen,
    setPeriodMenuOpen,
    activePeriod,
  } = useReturnPeriod(RETURN_PERIOD_STORAGE_KEY);

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) editMode.exitEditMode();
  }, [collapsed, editMode]);

  const handleSave = async (index: number, field: 'shares' | 'pricePerShare' | 'costBasis') => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updatePrivateEquityHolding(index, field, value, sheetId);
    }
  };

  const handleSaveText = async (index: number, field: 'name' | 'symbol') => {
    const { value, isValid } = completeEditText();
    if (isValid && sheetId) {
      await updatePrivateEquityHolding(index, field, value, sheetId);
    } else {
      cancelEdit();
    }
  };

  const handleAddClick = () => {
    if (isAtPrivateEquityLimit) {
      onUpgradeClick();
    } else {
      onAdd();
    }
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Private Equity"
        icon={<Building size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={visiblePrivateEquity.length}
            />
            <AddButton onClick={handleAddClick} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('privateEquity')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={visiblePrivateEquity.length}
          allSelected={editMode.selectedIndices.size === visiblePrivateEquity.length}
          onSelectAll={() => editMode.selectAll(visiblePrivateEquity.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      <div className="overflow-x-auto">
        {visiblePrivateEquity.length === 0 ? (
          <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <p className="text-sm">No private equity holdings yet</p>
            <p className="text-xs mt-1">Add shares in private companies you own</p>
          </div>
        ) : (
          <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2 md:[&_th]:px-1 md:[&_td]:px-1" style={{ minWidth: 650 }}>
            <thead>
              <tr
                className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${
                  darkMode ? 'border-white/10' : 'border-slate-200'
                }`}
              >
                {editMode.isEditMode && <th className="w-8 pb-2" />}
                <th className="text-left pb-2 whitespace-nowrap">Company</th>
                <th className="text-left pb-2 whitespace-nowrap w-[10%]">Symbol</th>
                <th className="text-right pb-2 whitespace-nowrap w-[13%]">Shares</th>
                <th className="text-right pb-2 whitespace-nowrap w-[14%]">Price/Share</th>
                <th className="text-right pb-2 whitespace-nowrap w-[14%]">Value</th>
                <th className="text-right pb-2 whitespace-nowrap w-[14%]">Cost</th>
                <ReturnPeriodHeader
                  darkMode={darkMode}
                  activeShort={activePeriod.short}
                  periodMenuOpen={periodMenuOpen}
                  returnPeriod={returnPeriod}
                  onToggleMenu={() => setPeriodMenuOpen(v => !v)}
                  onSelect={setPeriod}
                />
              </tr>
            </thead>
            <tbody>
              {visiblePrivateEquity.map((holding: PrivateEquity, idx: number) => {
                const percentage = pickPeriodReturn(
                  returnPeriod,
                  holding,
                  totalReturnPct(holding.costBasis, holding.totalValue)
                );
                return (
                  <tr
                    key={idx}
                    className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${
                      darkMode ? 'border-white/10' : 'border-slate-100'
                    }`}
                  >
                    {editMode.isEditMode && (
                      <td className="py-2 pr-2">
                        <EditModeCheckbox
                          checked={editMode.selectedIndices.has(idx)}
                          onToggle={() => editMode.toggleIndex(idx)}
                        />
                      </td>
                    )}
                    <td className="py-2 font-semibold max-w-[140px] truncate" title={holding.name}>
                      <InlineEditText
                        value={holding.name}
                        isEditing={isEditing('privateEquity', idx, 'name')}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={() => startEdit('privateEquity', idx, 'name', holding.name)}
                        onSave={() => handleSaveText(idx, 'name')}
                        onCancel={cancelEdit}
                        editable={holding.editable}
                        width="w-full"
                      />
                    </td>
                    <td className="py-2 font-mono">
                      <InlineEditText
                        value={holding.symbol || '-'}
                        isEditing={isEditing('privateEquity', idx, 'symbol')}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={() => startEdit('privateEquity', idx, 'symbol', holding.symbol)}
                        onSave={() => handleSaveText(idx, 'symbol')}
                        onCancel={cancelEdit}
                        editable={holding.editable}
                        width="w-full"
                      />
                    </td>
                    <td className="text-right">
                      <InlineEditField
                        value={holding.shares}
                        isEditing={isEditing('privateEquity', idx, 'shares')}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={() =>
                          startEdit('privateEquity', idx, 'shares', holding.shares)
                        }
                        onSave={() => handleSave(idx, 'shares')}
                        onCancel={cancelEdit}
                        editable={holding.editable}
                        variant="neutral"
                        width="w-full"
                        privacyMode={privacyMode}
                        formatFn={(v, p) => formatNumber(v, 2, p)}
                      />
                    </td>
                    <td className="text-right">
                      <InlineEditField
                        value={holding.pricePerShare}
                        isEditing={isEditing('privateEquity', idx, 'pricePerShare')}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={() =>
                          startEdit('privateEquity', idx, 'pricePerShare', holding.pricePerShare)
                        }
                        onSave={() => handleSave(idx, 'pricePerShare')}
                        onCancel={cancelEdit}
                        editable={holding.editable}
                        variant="positive"
                        width="w-full"
                        privacyMode={privacyMode}
                      />
                    </td>
                    <td className={`text-right font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {privacyMode ? '••••' : formatCurrency(holding.totalValue)}
                    </td>
                    <td className="text-right">
                      <InlineEditField
                        value={holding.costBasis}
                        isEditing={isEditing('privateEquity', idx, 'costBasis')}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={() =>
                          startEdit('privateEquity', idx, 'costBasis', holding.costBasis)
                        }
                        onSave={() => handleSave(idx, 'costBasis')}
                        onCancel={cancelEdit}
                        editable={holding.editable}
                        variant="neutral"
                        width="w-full"
                        privacyMode={privacyMode}
                      />
                    </td>
                    <td className="text-right">
                      <ReturnCell percentage={percentage} privacyMode={privacyMode} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {hasHiddenPrivateEquity && (
          <div className="mt-4">
            <UpgradePrompt
              count={hiddenPrivateEquityCount}
              type="holdings"
              onClick={onUpgradeClick}
            />
          </div>
        )}
        {visiblePrivateEquity.length > 0 && returnPeriod !== 'total' && (
          <p className={`text-[10px] mt-2 px-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Period returns need a public ticker in Symbol. Private-only names stay on Total.
          </p>
        )}
      </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm px-2 md:px-1">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total Private Equity
          </span>
          <span className={`${darkMode ? 'text-violet-400' : 'text-violet-600'} text-right`}>
            {formatCurrency(totalPrivateEquity, privacyMode)}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
